import { gql } from '@apollo/client';

import type { FileEditInput } from '@/lib/page-edits';

/**
 * Keystone's File surface, as the files harness reads it.
 *
 * The two operations are deliberately different shapes. `FILES_QUERY` is the
 * grid: one row per file and exactly **one** rendition per row, because a
 * `downloadUrl` is a V4 signature and an IAM round trip each — asking for
 * every label on a fifty-file page is fifty needless ones.
 * `FILE_DOCUMENT_QUERY` is the modal: one file, everything about it, and the
 * page rail.
 */

export type FileStatus = 'PENDING' | 'VALIDATED';
export type BinderyJobStatus = 'PENDING' | 'DISPATCHED' | 'COMPLETE' | 'FAILED';
export type CanonicalizationFilter = 'NONE' | BinderyJobStatus;

export interface BinderyJobSummary {
  id: string;
  status: BinderyJobStatus | null;
  verdict: string | null;
  failureClass: string | null;
  error: string | null;
  dispatchedAt: string | null;
  completedAt: string | null;
}

export interface Rendition {
  id: string;
  pageNo: number | null;
  width: number | null;
  height: number | null;
  downloadUrl: string;
}

export interface FileListRow {
  id: string;
  filename: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  pageCount: number | null;
  status: FileStatus | null;
  extAppApiId: string | null;
  createdAt: string | null;
  createdById: string | null;
  migratedAt: string | null;
  canonicalFileId: string | null;
  canonicalization: BinderyJobSummary | null;
  preview: Rendition[];
}

export interface FilesQueryResult {
  files: FileListRow[];
}

export interface FilesQueryVariables {
  status?: FileStatus | null;
  createdById?: string | null;
  extAppApiId?: string | null;
  hasExtAppApiId?: boolean | null;
  migrated?: boolean | null;
  canonicalized?: boolean | null;
  canonicalization?: CanonicalizationFilter | null;
  /** `false` hides canonical PDFs and extraction JSON — see FilesContainer. */
  derived?: boolean | null;
  limit: number;
  offset: number;
}

const BINDERY_JOB_FIELDS = `
  id
  status
  verdict
  failureClass
  error
  dispatchedAt
  completedAt
`;

export const FILES_QUERY = gql`
  query Files(
    $status: FileStatus
    $createdById: UUID
    $extAppApiId: String
    $hasExtAppApiId: Boolean
    $migrated: Boolean
    $canonicalized: Boolean
    $canonicalization: FileCanonicalizationFilter
    $derived: Boolean
    $limit: Int!
    $offset: Int!
  ) {
    files(
      status: $status
      createdById: $createdById
      extAppApiId: $extAppApiId
      hasExtAppApiId: $hasExtAppApiId
      migrated: $migrated
      canonicalized: $canonicalized
      canonicalization: $canonicalization
      derived: $derived
      limit: $limit
      offset: $offset
    ) {
      id
      filename
      contentType
      sizeBytes
      pageCount
      status
      extAppApiId
      createdAt
      createdById
      migratedAt
      canonicalFileId
      canonicalization { ${BINDERY_JOB_FIELDS} }
      preview: renditions(labels: [PREVIEW], page: 1) {
        id
        pageNo
        width
        height
        downloadUrl
      }
    }
  }
`;

export interface FileVersionRow {
  id: string;
  versionNo: number | null;
  createdAt: string | null;
  sizeBytes: number | null;
  pageCount: number | null;
  canonicalFileId: string | null;
}

export interface FileDocument {
  id: string;
  filename: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  checksum: string | null;
  pageCount: number | null;
  status: FileStatus | null;
  extAppApiId: string | null;
  createdAt: string | null;
  createdById: string | null;
  validatedAt: string | null;
  bucket: string | null;
  objectName: string | null;
  currentVersionId: string | null;
  duplicatedFromFileId: string | null;
  migratedAt: string | null;
  migratedById: string | null;
  migratedFromUri: string | null;
  originalDownloadUrl: string | null;
  canonicalDownloadUrl: string | null;
  canonicalFileId: string | null;
  canonicalFile: {
    id: string;
    filename: string | null;
    contentType: string | null;
    sizeBytes: number | null;
    pageCount: number | null;
    checksum: string | null;
  } | null;
  canonicalization: BinderyJobSummary | null;
  versions: FileVersionRow[];
  pages: Rendition[];
}

export interface FileDocumentQueryResult {
  file: FileDocument;
}

/**
 * The rail is windowed rather than unbounded. Keystone signs one URL per
 * rendition returned, so a thousand-page document would otherwise cost a
 * thousand IAM round trips to open — and the harness only needs enough of a
 * rail to see whether one was rendered at all. `pageCount` beside it is the
 * document's own, so the modal can say when it is showing part of a rail.
 */
export const RAIL_PAGE_LIMIT = 60;

export const FILE_DOCUMENT_QUERY = gql`
  query FileDocument($id: UUID!, $railLimit: Int!) {
    file(id: $id) {
      id
      filename
      contentType
      sizeBytes
      checksum
      pageCount
      status
      extAppApiId
      createdAt
      createdById
      validatedAt
      bucket
      objectName
      currentVersionId
      duplicatedFromFileId
      migratedAt
      migratedById
      migratedFromUri
      originalDownloadUrl
      canonicalDownloadUrl(disposition: INLINE)
      canonicalFileId
      canonicalFile {
        id
        filename
        contentType
        sizeBytes
        pageCount
        checksum
      }
      canonicalization { ${BINDERY_JOB_FIELDS} }
      versions {
        id
        versionNo
        createdAt
        sizeBytes
        pageCount
        canonicalFileId
      }
      pages: renditions(labels: [PAGE], limit: $railLimit, offset: 0) {
        id
        pageNo
        width
        height
        downloadUrl
      }
    }
  }
`;

/**
 * Soft-delete a File, and with it every version, canonical row and rendition.
 *
 * The harness offers this only where the file carries no `extAppApiId`: a
 * reference means an app-api record holds this id, and deleting underneath it
 * would leave that record resolving to nothing. Keystone itself imposes no
 * such rule — this is the portal declining to be the thing that breaks a join
 * it can see.
 */
export const DELETE_FILE = gql`
  mutation DeleteFile($id: UUID!) {
    deleteFile(id: $id)
  }
`;

export interface DeleteFileResult {
  deleteFile: boolean;
}

/**
 * Rotate, delete or mark a file's pages, producing its next version.
 *
 * Keystone answers at once with the file **unchanged** and the job to watch:
 * the head only moves when Bindery answers, and a failed edit never moves it.
 * `expectedVersion` is the head the rail was drawn from, so an edit authored
 * against bytes that have since changed is refused rather than applied.
 */
export const EDIT_FILE = gql`
  mutation EditFile($fileId: UUID!, $edits: FileEditInput!, $expectedVersion: Int) {
    editFile(fileId: $fileId, edits: $edits, expectedVersion: $expectedVersion) {
      versionNo
      job { ${BINDERY_JOB_FIELDS} }
    }
  }
`;

export interface EditFileResult {
  editFile: { versionNo: number; job: BinderyJobSummary };
}

export interface EditFileVariables {
  fileId: string;
  edits: FileEditInput;
  expectedVersion: number | null;
}

/**
 * An edit's outcome. `File.canonicalization` is the *head's* job, which a
 * failed edit leaves untouched, so this is the only field that can say what
 * happened to one.
 */
export const BINDERY_JOB_QUERY = gql`
  query BinderyJob($id: UUID!) {
    binderyJob(id: $id) { ${BINDERY_JOB_FIELDS} }
  }
`;

export interface BinderyJobQueryResult {
  binderyJob: BinderyJobSummary;
}

/** Move the head back onto an earlier version. Synchronous: no job is written. */
export const REVERT_FILE_VERSION = gql`
  mutation RevertFileVersion($fileId: UUID!, $versionNo: Int!) {
    revertFileVersion(fileId: $fileId, versionNo: $versionNo) {
      id
      currentVersionId
    }
  }
`;

export interface RevertFileVersionResult {
  revertFileVersion: { id: string; currentVersionId: string | null };
}
